import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTracking } from 'react-tracking';
import { AlloyButton, ComponentLoader } from 'cat-ecommerce-alloy';
import useSystemFeedback from '@app/hooks/useSystemFeedback';
import { ERROR_DOMAIN, ERROR_PATH } from '../../../../constants/errorConstants';
import ExceptionFeedbackBySelector from '../../Exception/ExceptionFeedbackBySelector';
import FormikSubmitErrorEffect from '../../form/FormikSubmitErrorEffect';
import AddressFormFields from '../AddressFormFields';
import { getAddressSubmitEventForFieldValidation } from '../analyticsUtils';
import { isSummaryAndPaymentPage } from '@app/utils/browserUtils';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import PageTitleHeader from '../../PageTitleHeader';
import SummaryAndPaymentCriticalErrorMessage from '@app/components/pages/summary-and-payment/SummaryAndPaymentCriticalErrorMessage/SummaryAndPaymentCriticalErrorMessage';
import ExceptionBlocksFeedbackBySelector from '../../ExceptionBlocks/ExceptionBlocksFeedbackBySelector';

const DEFAULT_COUNTRY_DISPLAY = 'United States';

const AddressFormModal = ({
  addressLimit,
  countries = [],
  defaultCountry,
  initialValues = {},
  isGuest = false,
  onClose,
  onSubmit,
  requiredFields = [],
  showPhoneNumberWithVerification,
  showNeighborhood,
  showAddButton = true,
  showFormLoader = false,
  isDealerUser = false,
  profileAddressId = '',
  labelClassName = '',
  showAddressLine2 = false
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { trackEvent } = useTracking();
  const { clearError } = useSystemFeedback();
  const isLoading = useSelector(state => state?.checkout?.aviLoading);
  const PCC_CustomerSupportErrorEventLoggingSummaryAndPayment = useSelector(
    state =>
      state?.featureFlag?.PCC_CustomerSupportErrorEventLoggingSummaryAndPayment
  );
  const verifyAddressTimeoutErrorSelector = state =>
    state?.errors?.[ERROR_DOMAIN.CHECKOUT]?.[ERROR_PATH.MODAL_TIMEOUT_ERROR];
  const verifyAddressTimeoutError = useSelector(
    verifyAddressTimeoutErrorSelector
  );
  const verifyAddressInlineErrorSelector = state =>
    state?.errors?.[ERROR_DOMAIN.CHECKOUT]?.[ERROR_PATH.MODAL_INLINE_ERROR];
  const verifyAddressInlineError = useSelector(
    verifyAddressInlineErrorSelector
  );
  const verifyAddressTimeoutBlocksErrorSelector = state =>
    state?.errors?.[ERROR_DOMAIN.CHECKOUT]?.[
      ERROR_PATH.MODAL_BLOCKS_TIMEOUT_ERROR
    ];
  const verifyAddressTimeoutBlocksError = useSelector(
    verifyAddressTimeoutBlocksErrorSelector
  );
  const showAddressName = !(
    isGuest ||
    (isSummaryAndPaymentPage() && isDealerUser)
  );
  const [isEditForm, setIsEditForm] = useState(!!initialValues.nickName);
  const isSelfAddressNonDealer =
    isSummaryAndPaymentPage() &&
    !isDealerUser &&
    !isEmpty(profileAddressId) &&
    profileAddressId == initialValues?.addressId;

  const switchToAddAddressForm = resetForm => {
    setIsEditForm(v => !v);
    resetForm({
      values: {
        nickName: '',
        firstName: '',
        lastName: '',
        country: defaultCountry || DEFAULT_COUNTRY_DISPLAY,
        zipCode: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        phone: '',
        email: ''
      }
    });
    dispatch(clearError(ERROR_DOMAIN.CHECKOUT));
  };

  const handleSubmit = (form, { setSubmitting }) => {
    clearError(ERROR_DOMAIN.CHECKOUT);
    // expose the setSubmitting (false) from formik
    // so a external component or proces can stop the spinner in the modal form
    if (showFormLoader) {
      const stopFormLoader = () => setSubmitting(false);
      return onSubmit(form, stopFormLoader);
    }
    return onSubmit(form);
  };

  const onFormikErrorHandle = newAddress => {
    const gaEvent = getAddressSubmitEventForFieldValidation({
      newAddress,
      isEditForm
    });
    trackEvent(gaEvent);
  };

  useEffect(() => {
    clearError(ERROR_DOMAIN.CHECKOUT);
  }, []);

  const getExceptionBlockFeedbackSelector = (
    errorSelector,
    isTimeout = false
  ) => (
    <ExceptionBlocksFeedbackBySelector
      className="mb-3"
      selector={errorSelector}
      showContactUs
      showReferenceId={!isTimeout}
      showRefresh={!isTimeout}
      showDate={isTimeout}
    />
  );

  return (
    <div className="u-rtl-text-align-right">
      <PageTitleHeader
        title={
          isEditForm ? t('ADDR_EDIT_ADDRESS') : t('ADD_ADDRESS_LINK_SHORT')
        }
        className={cx('mb-4', labelClassName)}
      />
      <div className="mt-3" />
      {PCC_CustomerSupportErrorEventLoggingSummaryAndPayment ? (
        <SummaryAndPaymentCriticalErrorMessage
          selector={state => state?.errors?.checkout?.modal}
        />
      ) : (
        <ExceptionFeedbackBySelector
          selector={state => state?.errors?.checkout?.modal}
        />
      )}
      {verifyAddressTimeoutError && (
        <ExceptionFeedbackBySelector
          selector={verifyAddressTimeoutErrorSelector}
          showContactUs
          showDate
        />
      )}

      {verifyAddressInlineError &&
        getExceptionBlockFeedbackSelector(verifyAddressInlineErrorSelector)}
      {verifyAddressTimeoutBlocksError &&
        getExceptionBlockFeedbackSelector(
          verifyAddressTimeoutBlocksErrorSelector,
          true
        )}

      <Formik
        onSubmit={handleSubmit}
        validateOnChange={false}
        initialValues={initialValues}
      >
        {({ resetForm, setFieldValue, values, isSubmitting }) => (
          <Form data-testid="address-form">
            {(isLoading || (showFormLoader && isSubmitting)) && (
              <ComponentLoader />
            )}
            <FormikSubmitErrorEffect callback={onFormikErrorHandle} />
            {isEditForm && !isGuest && showAddButton && (
              <AlloyButton
                buttonType="ghost"
                size="large"
                className={cx('mb-3 ps-0', labelClassName)}
                onClick={() => switchToAddAddressForm(resetForm)}
              >
                {t('ADD_NEW_ADDRESS')}
              </AlloyButton>
            )}
            <AddressFormFields
              autoCompleteOnZipcode
              isAddressNameEditable={!isEditForm || isSelfAddressNonDealer}
              menuSize="small"
              showAddressLine2={!!values.address2 || showAddressLine2}
              showAddressName={showAddressName}
              showEmail
              showName
              showPhoneNumber
              countryData={countries}
              addressLimit={addressLimit}
              requiredFields={requiredFields}
              onCountryChange={() => setFieldValue('state', '')}
              selectedCountry={values.country}
              nickName={values.nickName}
              showPhoneNumberWithVerification={showPhoneNumberWithVerification}
              showNeighborhood={showNeighborhood}
              isDealerUser={isDealerUser}
              profileAddressId={profileAddressId}
              labelClassName={labelClassName}
            />
            <p
              className={cx(
                'text-xs u-color--DarkGray mb-2 pb-1',
                labelClassName
              )}
            >
              {t('CAT_PARTS_MANUALS_REQ_FIELDS')}
            </p>
            <div className="d-flex flex-column">
              <div className="text-center">
                <AlloyButton
                  className={labelClassName}
                  type="submit"
                  buttonType="secondary"
                  size="large"
                >
                  {isEditForm ? t('UPDATE') : t('SAVE')}
                </AlloyButton>
              </div>
              <div className="text-center">
                <AlloyButton
                  className={labelClassName}
                  buttonType="ghost"
                  size="large"
                  onClick={onClose}
                >
                  {t('DELETE_PAYMENT_METHOD_CANCEL')}
                </AlloyButton>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

AddressFormModal.propTypes = {
  addressLimit: PropTypes.string,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      displayName: PropTypes.string,
      states: PropTypes.arrayOf(
        PropTypes.shape({
          code: PropTypes.string,
          displayName: PropTypes.string
        })
      )
    })
  ),
  defaultCountry: PropTypes.string,
  initialValues: PropTypes.shape({
    nickName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    country: PropTypes.string,
    zipCode: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string
  }),
  isGuest: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  requiredFields: PropTypes.arrayOf(PropTypes.string),
  showPhoneNumberWithVerification: PropTypes.bool,
  showNeighborhood: PropTypes.bool,
  showAddButton: PropTypes.bool,
  showFormLoader: PropTypes.bool,
  isDealerUser: PropTypes.bool,
  profileAddressId: PropTypes.string,
  labelClassName: PropTypes.string,
  showAddressLine2: PropTypes.bool
};

export default AddressFormModal;
